import React from "react";
import { Target, Disc3, Mic2, Music, Train, ParkingCircle } from "lucide-react";

const POOL =
  "https://images.pexels.com/photos/5055749/pexels-photo-5055749.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TILES = [
  {
    id: "pool",
    icon: <Disc3 size={22} strokeWidth={2.5} />,
    title: "POOL",
    sub: "Quarter-fed tables. Bring change.",
  },
  {
    id: "darts",
    icon: <Target size={22} strokeWidth={2.5} />,
    title: "DARTS",
    sub: "Aim true. Miss often.",
  },
  {
    id: "foosball",
    icon: <Disc3 size={22} strokeWidth={2.5} />,
    title: "FOOSBALL",
    sub: "House rules apply.",
  },
  {
    id: "jukebox",
    icon: <Music size={22} strokeWidth={2.5} />,
    title: "JUKEBOX",
    sub: "Bring quarters and taste.",
  },
  {
    id: "karaoke",
    icon: <Mic2 size={22} strokeWidth={2.5} />,
    title: "KARAOKE",
    sub: "Pick something dramatic.",
  },
];

const DIRECTIONS = [
  {
    id: "metrorail",
    icon: <Train size={18} strokeWidth={2.5} />,
    title: "METRORail",
    body: "1 block from Central Station (Red Line). Bell Station is a 4-min walk.",
  },
  {
    id: "toyota-center",
    icon: <ParkingCircle size={18} strokeWidth={2.5} />,
    title: "Toyota Center",
    body: "5-min walk south. Perfect pre-game / post-concert stop.",
  },
  {
    id: "minute-maid",
    icon: <ParkingCircle size={18} strokeWidth={2.5} />,
    title: "Minute Maid Park",
    body: "8-min walk northeast. Astros crowd lives here on game days.",
  },
];

const GameTile = ({ tile }) => (
  <div
    data-testid={`game-tile-${tile.id}`}
    className="bg-black/70 backdrop-blur border border-white/10 p-3 hover:border-[#FFB800] transition-colors"
  >
    <div className="text-[#FFB800]">{tile.icon}</div>
    <div className="mt-2 font-display text-xl text-[#F5F2E6] leading-none">
      {tile.title}
    </div>
    <div className="mt-1 font-mono-plex text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3]">
      {tile.sub}
    </div>
  </div>
);

const DirectionTip = ({ tip }) => (
  <div
    data-testid={`direction-${tip.id}`}
    className="border border-[#262626] hover:border-[#FFB800]/40 transition-colors p-5 bg-[#121212]"
  >
    <div className="flex items-center gap-2 text-[#FFB800]">
      {tip.icon}
      <span className="font-mono-plex text-[10px] tracking-[0.25em] uppercase">
        {tip.title}
      </span>
    </div>
    <p className="mt-3 text-[#F5F2E6] text-sm md:text-base leading-relaxed">
      {tip.body}
    </p>
  </div>
);

const GamesAndDirections = () => {
  return (
    <section
      id="games"
      data-testid="games-section"
      className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="font-mono-plex text-[11px] tracking-[0.3em] uppercase text-[#FFB800] flex items-center gap-3">
            <span className="h-px w-8 bg-[#FFB800]" /> 04 — Games &amp; The
            Neighborhood
          </div>
          <h2 className="mt-4 font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-[#F5F2E6]">
            STAY <span className="text-[#FF2A2A]">LONGER</span>
            <br />
            THAN ONE DRINK.
          </h2>
          <p className="mt-6 text-lg text-[#A3A3A3] max-w-md leading-relaxed">
            Quarter-fed pool tables, beat-up foosball, a jukebox that takes
            requests in cash, and a back room that's been hosting strangers
            since before downtown was cool.
          </p>

          <div className="mt-8 space-y-3">
            {DIRECTIONS.map((tip) => (
              <DirectionTip key={tip.id} tip={tip} />
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="grain-card relative border border-[#262626] overflow-hidden">
            <img
              src={POOL}
              alt="Friends playing pool at Sunny's Bar quarter-fed pool table in downtown Houston"
              className="w-full h-[360px] md:h-[500px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 grid grid-cols-2 md:grid-cols-5 gap-2">
              {TILES.map((tile) => (
                <GameTile key={tile.id} tile={tile} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GamesAndDirections;
